import React, {useEffect, useRef} from 'react';
import {connect} from "react-redux";

import 'leaflet/dist/leaflet.css';
import {MapContainer, Marker, Polyline, Popup, TileLayer, useMap, useMapEvents} from "react-leaflet";
import L from 'leaflet';
import {Icon} from 'leaflet'
import {DEFAULT_THEME_COLOR} from "../../constants/theme";
import "./FreakLocalization.css";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import useTranslation from "../../customHooks/translations";

const createPhotoBubbleIcon = (imageUrl) => L.divIcon({
  html: `<div style="width:48px;height:48px;border-radius:50%;border:2px solid ${DEFAULT_THEME_COLOR};overflow:hidden;box-shadow:0 2px 6px rgba(0,0,0,0.4);"><img src="${imageUrl}" style="width:100%;height:100%;object-fit:cover;" /></div>`,
  className: '',
  iconSize: [48, 48],
  iconAnchor: [24, 24],
  popupAnchor: [0, -28],
});

// colours for other members' tracks (the main route keeps the theme colour)
export const TRACK_COLORS = ['#ff9f43', '#4facfe', '#ee5a6f', '#a55eea', '#feca57', '#1dd1a1', '#ff6b81', '#54a0ff'];

const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, c => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

const createLiveMemberIcon = (track, color) => L.divIcon({
  html: `<div style="width:36px;height:36px;border-radius:50%;border:3px solid ${color};overflow:hidden;background:#0d1b2a;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,0.4);">${
    track.imageUrl
      ? `<img src="${escapeHtml(track.imageUrl)}" style="width:100%;height:100%;object-fit:cover;" />`
      : escapeHtml(track.username.charAt(0).toUpperCase())
  }</div>`,
  className: '',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
});

// Fits the map to the given positions, but only when fitKey changes (not on every refresh),
// so periodic live-location updates don't fight the user's panning/zooming.
// `positions` is intentionally not a dependency.
const FitBoundsOnChange = ({positions, fitKey}) => {
  const map = useMap();

  useEffect(() => {
    if (positions.length >= 2) {
      map.fitBounds(L.latLngBounds(positions));
    }
  }, [map, fitKey]);

  return null;
};

// "you are here" dot for the track recorder
const LIVE_DOT_ICON = L.divIcon({
  html: '<div style="width:20px;height:20px;border-radius:50%;background:#2f80ed;border:3px solid white;box-shadow:0 0 0 8px rgba(47,128,237,0.22),0 2px 6px rgba(0,0,0,0.4);"></div>',
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Track recorder: keeps the map on the user at the current zoom while `follow` is on.
const FollowUser = ({position}) => {
  const map = useMap();
  const lat = position?.[0];
  const lng = position?.[1];

  useEffect(() => {
    if (lat != null && lng != null) {
      map.panTo([lat, lng], {animate: true});
    }
  }, [map, lat, lng]);

  return null;
};

// Tells the recorder the user dragged the map, so it stops following.
const UserPanListener = ({onUserPan}) => {
  useMapEvents({dragstart: () => onUserPan()});
  return null;
};

const minutesAgo = at => Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60000));

// colour per member: author = main route colour, others cycle through TRACK_COLORS
export const getTrackColors = (tracks = []) => {
  let index = 0;
  return tracks.reduce((acc, track) => ({
    ...acc,
    [track.username]: track.isAuthor ? DEFAULT_THEME_COLOR : TRACK_COLORS[index++ % TRACK_COLORS.length],
  }), {});
};

const getContextTrackingComponent = (
  translations,
  context,
  distance= 0.0,
  altitude = 0,
  maxSpeed = 0.0,
  movingTime = 0.0,
  maxAltitude = 0,
  avgSpeed = 0,
) => {
  return (
    <>
      <Grid container sx={{
        marginTop: 3,
        marginBottom: 3,
      }}>
        <Grid item xs={4} sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'left'
        }}>
          <Grid container>
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left'
            }}>
              <Typography sx={{
                marginLeft: 1,
                fontSize: 13,
                color: 'rgb(82, 88, 102)'
              }}>
                {translations.freakLocalizationTracker.distance}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{marginLeft: 1, color: 'black'}}>
                {distance} km
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
          <Grid container>
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'left'
            }}>
              <Typography sx={{
                fontSize: 13,
                color: 'rgb(82, 88, 102)'
              }}>
                {translations.freakLocalizationTracker.altitude}
              </Typography>
            </Grid>
            <Grid item xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'left'
                  }}>
              <Typography variant="h6" sx={{color: 'black'}}>
                {altitude} m
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'right'
              }}>
          <Grid container>
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'left'
            }}>
              <Typography sx={{
                marginLeft: 1,
                fontSize: 13,
                color: 'rgb(82, 88, 102)'
              }}>
                {translations.freakLocalizationTracker.maxSpeed}
              </Typography>
            </Grid>
            <Grid item xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'right'
                  }}>
              <Typography variant="h6" sx={{marginLeft: 1, color: 'black'}}>
                {maxSpeed} km/h
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container sx={{
        marginTop: 3,
        marginBottom: 3,
      }}>
        <Grid item xs={4} sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'left'
        }}>
          <Grid container>
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left'
            }}>
              <Typography sx={{
                marginLeft: 1,
                fontSize: 13,
                color: 'rgb(82, 88, 102)'
              }}>
                {translations.freakLocalizationTracker.movingTime}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{marginLeft: 1, color: 'black'}}>
                {movingTime}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
          <Grid container>
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'left'
            }}>
              <Typography sx={{
                fontSize: 13,
                color: 'rgb(82, 88, 102)'
              }}>
                {translations.freakLocalizationTracker.maxAltitude}
              </Typography>
            </Grid>
            <Grid item xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'left'
                  }}>
              <Typography variant="h6" sx={{color: 'black'}}>
                {maxAltitude} m
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'right'
              }}>
          <Grid container>
            <Grid item xs={12} sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'left'
            }}>
              <Typography sx={{
                marginLeft: 1,
                fontSize: 13,
                color: 'rgb(82, 88, 102)'
              }}>
                {translations.freakLocalizationTracker.avgSpeed}
              </Typography>
            </Grid>
            <Grid item xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'right'
                  }}>
              <Typography variant="h6" sx={{marginLeft: 1, color: 'black'}}>
                {avgSpeed} km/h
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  )
};

const FreakLocalizationTracker = (props) => {
  const translations = useTranslation();
  const mapRef = useRef(null);

  const context = props.context || 'wof';
  const defaultPosition = [50.258598, 19.020420];
  const position = props?.position || defaultPosition;
  const defaultZoom = 16;
  const zoom = props.zoom || defaultZoom;
  const height = props.height || '45vh';

  const tracks = props.tracks || [];
  const trackColors = getTrackColors(tracks);
  const memberTracks = tracks.filter(track => !track.isAuthor && track.route?.length >= 2);
  const liveMembers = tracks.filter(track => track.liveLocation && !track.isMe);
  const fitPositions = [
    ...(props.routeCoordinates || []),
    ...memberTracks.flatMap(track => track.route),
    ...liveMembers.map(track => [track.liveLocation.latitude, track.liveLocation.longitude]),
  ];

  // Get the start and end positions
  const startPosition = props?.routeCoordinates?.length ? props?.routeCoordinates[0] : null;
  const endPosition = props?.routeCoordinates?.length ? props?.routeCoordinates[props?.routeCoordinates.length - 1] : null;

  // Function to center the map on the user's current location with higher zoom
  const FitMapToUser = ({position, zoomLevel = 18}) => {
    const map = useMap();

    useEffect(() => {
      if (position && position.length) {
        // Create a LatLng object for the user's current position
        const userLatLng = L.latLng(position[0], position[1]);

        // map.flyTo(userLatLng, zoomLevel, {
        //   animate: true,
        //   duration: 1.5,
        // });
        map.setView(userLatLng, zoomLevel);
      }
    }, [map, position, zoomLevel]);

    return null; // This component does not render anything
  };

  // Define custom start and end icons
  const startIcon = L.icon({
    iconUrl: '/icons/pin.png', // Path to the start icon image
    iconSize: [32, 32], // Size of the icon
    iconAnchor: [16, 32], // Anchor point of the icon (bottom center)
  });

  const endIcon = L.icon({
    iconUrl: '/icons/pin.png', // Path to the end icon image
    iconSize: [32, 32], // Size of the icon
    iconAnchor: [16, 32], // Anchor point of the icon (bottom center)
  });

  return (
    position
      ? <>
        <MapContainer
          center={position}
          zoom={zoom}
          style={{height, width: '100%'}}
          ref={mapRef}
          attributionControl={false}
          zoomControl={!props.hideZoomControl}
          className="freak-map"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            // url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
          />
          {
            position
              ?
              <Marker
                position={position}
                icon={props.liveDot
                  ? LIVE_DOT_ICON
                  : new Icon({
                    iconUrl: '/icons/marker-public-icon.png',
                    iconSize: [32, 32],
                    iconAnchor: [16, 32]
                  }
                )}
              >
              </Marker>
              : null
          }

          {
            props.routeCasing && props?.routeCoordinates?.length >= 2
              ? <Polyline
                pathOptions={{color: 'white', weight: 10, opacity: 1, lineJoin: 'round', lineCap: 'round'}}
                positions={props.routeCoordinates}
              />
              : null
          }

          {
            props?.routeCoordinates?.length >= 2
              ? <Polyline
                pathOptions={{
                  color: DEFAULT_THEME_COLOR,
                  weight: props.routeCasing ? 6 : 4,
                  opacity: 0.9,
                  lineJoin: 'round',
                  lineCap: 'round'
                }}
                positions={props.routeCoordinates}
              />
              : null
          }

          {
            startPosition
              ? <Marker position={startPosition} icon={startIcon}/>
              : null
          }

          {
            !props.isRunning && endPosition
              ? <Marker position={endPosition} icon={endIcon}/>
              : null
          }

          {memberTracks.map(track => (
            <Polyline
              key={`track-${track.username}`}
              pathOptions={{
                color: trackColors[track.username],
                weight: 4,
                opacity: 0.9,
                lineJoin: 'round',
                lineCap: 'round'
              }}
              positions={track.route}
            />
          ))}

          {liveMembers.map(track => (
            <Marker
              key={`live-${track.username}`}
              position={[track.liveLocation.latitude, track.liveLocation.longitude]}
              icon={createLiveMemberIcon(track, trackColors[track.username])}
            >
              <Popup>
                <strong>@{track.username}</strong>
                <br/>
                {translations.freakLocalizationTracker.liveMinutesAgo.replace('{count}', minutesAgo(track.liveLocation.at))}
              </Popup>
            </Marker>
          ))}

          {
            !props.isRunning && fitPositions.length >= 2
              ? <FitBoundsOnChange
                positions={fitPositions}
                fitKey={`${props.routeCoordinates?.length || 0}:${memberTracks.map(track => track.username).join(',')}`}
              />
              : null
          }
          {
            // the recorder passes `follow`; other screens keep the old fit-to-user while running
            props.follow !== undefined
              ? (props.follow ? <FollowUser position={position}/> : null)
              : props.isRunning
                ? <FitMapToUser position={position}/>
                : null
          }
          {props.onUserPan ? <UserPanListener onUserPan={props.onUserPan}/> : null}
          {(props.photos || []).map((photo, idx) => (
            <Marker
              key={photo.id || idx}
              position={[photo.latitude, photo.longitude]}
              icon={createPhotoBubbleIcon(photo.imageUrl)}
            >
              <Popup>
                <img
                  src={photo.imageUrl}
                  alt=""
                  style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: '50%', display: 'block' }}
                />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {tracks.length > 1 || liveMembers.length > 0
          ? <div style={{display: 'flex', flexWrap: 'wrap', gap: 12, padding: '8px 12px'}}>
            {tracks.map(track => (
              <span key={`legend-${track.username}`} style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13}}>
                <span style={{width: 14, height: 4, borderRadius: 2, background: trackColors[track.username]}}/>
                {track.isMe ? translations.freakLocalizationTracker.you : `@${track.username}`}
                {track.liveLocation ? ` · ${translations.freakLocalizationTracker.live}` : ''}
              </span>
            ))}
          </div>
          : null}
        {/*{getContextTrackingComponent(translations, context, props.distance)}*/}
      </>
      : null
  )
};

const mapStateToProps = state => ({
  context: state.common.context,
});
const mapDispatchToProps = dispatch => ({});

export default connect(mapStateToProps, mapDispatchToProps)(FreakLocalizationTracker);
